export type Currency = 'USD' | 'EUR' | 'BRL' | 'PLN';

interface CurrencyConfig {
  code: Currency;
  symbol: string;
  name: string;
  locale: string;
  decimals: number;
}

export const CURRENCY_CONFIGS: Record<Currency, CurrencyConfig> = {
  USD: { code: 'USD', symbol: '$', name: 'US Dollar', locale: 'en-US', decimals: 2 },
  EUR: { code: 'EUR', symbol: '€', name: 'Euro', locale: 'en-GB', decimals: 2 },
  BRL: { code: 'BRL', symbol: 'R$', name: 'Brazilian Real', locale: 'pt-BR', decimals: 2 },
  PLN: { code: 'PLN', symbol: 'zł', name: 'Polish Zloty', locale: 'pl-PL', decimals: 2 },
};

/**
 * Format a number as currency
 */
export function formatCurrency(
  amount: number,
  currency: Currency = 'USD',
  options?: Intl.NumberFormatOptions
): string {
  const config = CURRENCY_CONFIGS[currency];

  if (!config) {
    console.warn(`Unknown currency: ${currency}, falling back to USD`);
    return formatCurrency(amount, 'USD', options);
  }

  // Use 0 decimal places for whole numbers, 2 for decimals
  const isWholeNumber = amount === Math.floor(amount);

  return new Intl.NumberFormat(config.locale, {
    style: 'currency',
    currency: config.code,
    minimumFractionDigits: isWholeNumber ? 0 : config.decimals,
    maximumFractionDigits: config.decimals,
    ...options,
  }).format(amount);
}

/**
 * Get currency symbol
 */
export function getCurrencySymbol(currency: Currency): string {
  return CURRENCY_CONFIGS[currency]?.symbol || '$';
}

/**
 * Parse currency string to number
 */
export function parseCurrency(value: string): number {
  const cleaned = value.replace(/[^\d.-]/g, '');
  return parseFloat(cleaned) || 0;
}
